import { Button, TypographyProps } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import PageTabsV2 from "@/App/Layouts/PageBody/PageTabsV2";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AuthorizedTenantElement from "@/common/components/Auth/AuthorizedTenantElement";
import AppIcon from "@/common/components/Icons/AppIcon";
import { AppModalCloseIconButton } from "@/common/components/Modals/AppModalCloseIconButton";
import { PersistenceKey } from "@/common/constants/persistenceKey";
import { ROUTE_PATH } from "@/common/constants/routing";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useCurrentTenant } from "@/common/hooks/entity/tenant/useCurrentTenant";
import { useCommonViewParams } from "@/common/hooks/layout/useCommonViewParams";
import { useRealtimeDataUpdates } from "@/common/hooks/realtime/useRealtimeDataUpdates";
import { useAppHistory } from "@/common/hooks/routing/useAppHistory";
import { DataUpdatesChannelName } from "@/common/realtime/dataUpdatesChannelName";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  DataUpdatesHubClientMethodName,
  EntityType,
  MaintenanceDto,
  VehicleOperationType,
} from "@/core/api/generated/v1";

import { VehicleOperationViewPageHeader } from "../../Vehicle/View/Header/VehicleOperationViewPageHeader";
import BaseEntityView, { BaseEntityViewInheritableProps } from "../../components/BaseEntityView";
import MaintenanceMenu from "../MaintenanceMenu";
import CommentsTabContent from "./Tabs/CommentsTabContent";
import GeneralHistoryTabContent from "./Tabs/GeneralHistoryTabContent";
import InboxTabContent from "./Tabs/InboxTabContent";
import OverviewTabContent from "./Tabs/OverviewTabContent";
import VehicleTabContent from "./Tabs/VehicleTabContent";

export enum MaintenanceViewPageTabs {
  Overview = "Overview",
  Inbox = "Inbox",
  Vehicle = "Vehicle",
  GeneralHistory = "GeneralHistory",
  Comments = "Comments",
}

const defaultDisplayProps = {
  breadcrumbs: true,
  header: true,
  actions: true,
  closeInHeader: true,
  viewVariant: ViewVariant.Page,
};

const defaultHeaderProps = {
  withLink: false,
  typographyProps: undefined as Partial<TypographyProps> | undefined,
};

export interface MaintenanceViewProps extends BaseEntityViewInheritableProps<MaintenanceDto> {
  maintenanceId: string | null | undefined;
  maintenance: MaintenanceDto | null | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  headerProps?: Partial<typeof defaultHeaderProps>;
}

export default function MaintenanceView({
  maintenanceId,
  maintenance,
  displayProps,
  headerProps,
  onUpdate,
  onDelete,
  onClose,
}: MaintenanceViewProps) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const history = useAppHistory();
  const currentTenant = useCurrentTenant();
  const commonViewParams = useCommonViewParams({
    statePersistence: {
      persistenceKey: PersistenceKey.forEntityView(EntityType.Maintenance),
      viewVariant: displayProps?.viewVariant,
    },
  });

  const maintenanceRequest = useApiRequest(
    apiClient.maintenancesApi.apiV1MaintenancesMaintenanceIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      maintenanceId: maintenanceId!,
    },
    {
      deps: [maintenanceId],
      skip: !maintenanceId,
    },
  );
  maintenance = maintenance || maintenanceRequest?.data;

  useRealtimeDataUpdates({
    channelNames: [
      DataUpdatesChannelName.Entity(currentTenant?.id, EntityType.Maintenance, maintenanceId || ""),
    ],
    methodNames: [DataUpdatesHubClientMethodName.EntityChanged],
    handler: undefined,
    entityChangedHandler: (methodName, data) => {
      maintenanceRequest.handleEntityChanged(data);
    },
  });

  useBreadcrumbReplacements({
    waitTimeout: 10_000,
    idBreadcrumb: maintenance && {
      idValue: maintenance?.id || "",
      newTitle: maintenance?.localNumber || "",
    },
  });

  return (
    <BaseEntityView
      entityType={EntityType.Maintenance}
      entityId={maintenanceId}
      entity={maintenance}
      entityRequest={maintenanceRequest}
    >
      <ViewLayout
        displayProps={displayProps}
        header={
          maintenance && (
            <VehicleOperationViewPageHeader
              operationType={VehicleOperationType.Maintenance}
              operationEntity={maintenance}
              vehicleId={maintenance?.vehicle?.id}
              vehicle={maintenance?.vehicle || undefined}
              titleProps={{
                to: headerProps?.withLink
                  ? ROUTE_PATH.MAINTENANCE_VIEW(maintenance?.id)
                  : undefined,
                typographyProps: headerProps?.typographyProps,
              }}
              primaryActions={
                maintenance && (
                  <AuthorizedTenantElement tenantIds={[maintenance?.tenantId]}>
                    <AuthorizedElement permissions={[AppPermission.MaintenanceManage]}>
                      <Button
                        variant='outlined'
                        color='text'
                        component={RouterLink}
                        to={ROUTE_PATH.MAINTENANCE_EDIT(maintenance?.id)}
                        startIcon={<AppIcon of='edit' />}
                      >
                        Edit
                      </Button>
                    </AuthorizedElement>
                  </AuthorizedTenantElement>
                )
              }
              secondaryActions={
                <MaintenanceMenu
                  entity={maintenance}
                  displayProps={{
                    actions: {
                      edit: false,
                      delete: true,
                      addToAssignment: true,
                    },
                  }}
                  onUpdate={(newValue) => {
                    newValue
                      ? maintenanceRequest.replaceData(newValue)
                      : maintenanceRequest.refetch();
                    onUpdate?.(newValue);
                  }}
                  onDelete={() => (onDelete ? onDelete() : history.goBack())}
                />
              }
              closeAction={
                displayProps.closeInHeader && onClose ? (
                  <AppModalCloseIconButton onClick={onClose} />
                ) : undefined
              }
              onTagsUpdated={(newValue) => {
                maintenanceRequest.updateData((x) => {
                  x.tags = newValue || undefined;
                  onUpdate?.(x);
                });
              }}
            />
          )
        }
      >
        <PageTabsV2
          tabIdsDefinition={MaintenanceViewPageTabs}
          defaultTabId={MaintenanceViewPageTabs.Overview}
          commonViewParams={commonViewParams}
          tabs={[
            { label: "Overview", value: MaintenanceViewPageTabs.Overview, isHideable: false },
            { label: "Chats", value: MaintenanceViewPageTabs.Inbox },
            {
              label: "Vehicle",
              value: MaintenanceViewPageTabs.Vehicle,
              permissions: [AppPermission.VehicleRead],
            },
            {
              label: "History",
              value: MaintenanceViewPageTabs.GeneralHistory,
              permissions: [AppPermission.GeneralHistoryRead],
            },
            { label: "Comments", value: MaintenanceViewPageTabs.Comments },
          ]}
        >
          {({ activeTabId: activeTab }) =>
            maintenance && (
              <>
                {activeTab === MaintenanceViewPageTabs.Overview && (
                  <OverviewTabContent
                    onUpdate={(newValue) => {
                      newValue
                        ? maintenanceRequest.replaceData(newValue)
                        : maintenanceRequest.refetch();
                      onUpdate?.(newValue);
                    }}
                    maintenance={maintenance}
                  />
                )}
                {activeTab === MaintenanceViewPageTabs.Inbox && (
                  <InboxTabContent maintenance={maintenance} />
                )}
                {activeTab === MaintenanceViewPageTabs.Vehicle && (
                  <VehicleTabContent maintenance={maintenance} />
                )}
                {activeTab === MaintenanceViewPageTabs.GeneralHistory && (
                  <GeneralHistoryTabContent maintenance={maintenance} />
                )}
                {activeTab === MaintenanceViewPageTabs.Comments && (
                  <CommentsTabContent maintenance={maintenance} />
                )}
              </>
            )
          }
        </PageTabsV2>
      </ViewLayout>
    </BaseEntityView>
  );
}
